export type TransportScenarioParam = 'emfac2021' | 'emfac2025';
export type BuildingsScenarioParam = 'bau' | 'current_policy' | 'conservative' | 'accelerated';
export type LocalPresetParam = 'baseline' | 'moderate' | 'aggressive' | 'fullImplementation';

export type ScenarioUrlParams = {
  transport?: TransportScenarioParam;
  buildings?: BuildingsScenarioParam;
  local?: LocalPresetParam;
  ev?: number;
  vmt?: number;
  elecYear?: number;
  energyEfficiency?: number;
  buildingsElectrification?: number;
  organics?: number;
  compactDevelopment?: number;
  downtownHomes?: number;
  activeTransportation?: number;
  transitImprovements?: number;
  parkingPricing?: number;
  micromobility?: number;
  weatherization?: number;
  residentialReachCode?: number;
  commercialReachCode?: number;
  demandResponse?: number;
  otherEfficiency?: number;
  scenarioId?: string;
};

export type ScenarioStateCore = {
  location: string;
  emfacVersion: '2021' | '2025';
  buildingsBauScenario: 'conservative' | 'current_policy' | 'accelerated';
  localScenarioName?: LocalPresetParam;
  interventionAreas: {
    vehicleElectrification: number;
    vmtReduction: number;
    energyEfficiency?: number;
    buildingsElectrification?: number;
    gridDecarbonization: number;
    organicsDiversion?: number;
  };
  subPolicyValues?: {
    compactDevelopment?: number;
    downtownHomesPercent?: number;
    activeTransportation?: number;
    transitImprovements?: number;
    parkingPricing?: number;
    micromobility?: number;
  };
  energyEfficiencyComponents?: {
    weatherizationPercent?: number;
    residentialReachCodePercent?: number;
    commercialReachCodePercent?: number;
    demandResponsePercent?: number;
    otherEfficiencyPercent?: number;
  };
};

export type ValidationResult = {
  params: ScenarioUrlParams;
  invalidKeys: string[];
};

type PresetValues = { ev: number; vmt: number; elecYear: number };

const TRANSPORT_VALUES = new Set<TransportScenarioParam>(['emfac2021', 'emfac2025']);
const BUILDINGS_VALUES = new Set<BuildingsScenarioParam>(['bau', 'current_policy', 'conservative', 'accelerated']);
const LOCAL_VALUES = new Set<LocalPresetParam>(['baseline', 'moderate', 'aggressive', 'fullImplementation']);
const PRESET_DEFAULTS: Record<LocalPresetParam, PresetValues> = {
  baseline: { ev: 0, vmt: 0, elecYear: 2045 },
  moderate: { ev: 50, vmt: 4, elecYear: 2030 },
  aggressive: { ev: 80, vmt: 7, elecYear: 2025 },
  fullImplementation: { ev: 100, vmt: 14, elecYear: 2025 },
};

function isDev(): boolean {
  return typeof process !== 'undefined' && process.env.NODE_ENV === 'development';
}

function devLog(message: string, payload?: unknown): void {
  if (!isDev()) return;
  if (payload !== undefined) console.log(`[scenarioUrl] ${message}`, payload);
  else console.log(`[scenarioUrl] ${message}`);
}

function toNumberOrUndefined(value: string | null): number | undefined {
  if (value == null || value.trim() === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Backward-compatible normalization:
 * - supports ev=72 and ev=0.72
 * - supports vmt=7 and vmt=0.07
 * Internal/UI representation remains percentage-like numbers (0-100 scale).
 */
function normalizePercentLike(value: number | undefined, maxPercent: number): number | undefined {
  if (value == null || !Number.isFinite(value)) return undefined;
  const normalized = value >= 0 && value <= 1 ? value * 100 : value;
  return clamp(normalized, 0, maxPercent);
}

export function parseScenarioFromUrl(search?: string): ScenarioUrlParams {
  if (typeof window === 'undefined' && search == null) return {};
  const raw = search ?? window.location.search;
  const params = new URLSearchParams(raw);
  const parsed: ScenarioUrlParams = {
    transport: (params.get('transport') as TransportScenarioParam | null) ?? undefined,
    buildings: (params.get('buildings') as BuildingsScenarioParam | null) ?? undefined,
    local: (params.get('local') as LocalPresetParam | null) ?? undefined,
    ev: toNumberOrUndefined(params.get('ev')),
    vmt: toNumberOrUndefined(params.get('vmt')),
    elecYear: toNumberOrUndefined(params.get('elecYear')),
    energyEfficiency: toNumberOrUndefined(params.get('energyEfficiency')),
    buildingsElectrification: toNumberOrUndefined(params.get('buildingsElectrification')),
    organics: toNumberOrUndefined(params.get('organics')),
    compactDevelopment: toNumberOrUndefined(params.get('compactDevelopment')),
    downtownHomes: toNumberOrUndefined(params.get('downtownHomes')),
    activeTransportation: toNumberOrUndefined(params.get('activeTransportation')),
    transitImprovements: toNumberOrUndefined(params.get('transitImprovements')),
    parkingPricing: toNumberOrUndefined(params.get('parkingPricing')),
    micromobility: toNumberOrUndefined(params.get('micromobility')),
    weatherization: toNumberOrUndefined(params.get('weatherization')),
    residentialReachCode: toNumberOrUndefined(params.get('residentialReachCode')),
    commercialReachCode: toNumberOrUndefined(params.get('commercialReachCode')),
    demandResponse: toNumberOrUndefined(params.get('demandResponse')),
    otherEfficiency: toNumberOrUndefined(params.get('otherEfficiency')),
    scenarioId: params.get('scenarioId') || undefined,
  };
  devLog('parsed URL params', parsed);
  return parsed;
}

export function validateScenarioUrlParams(parsed: ScenarioUrlParams): ValidationResult {
  const params: ScenarioUrlParams = {};
  const invalidKeys: string[] = [];

  if (parsed.transport && TRANSPORT_VALUES.has(parsed.transport)) params.transport = parsed.transport;
  else if (parsed.transport != null) invalidKeys.push('transport');

  if (parsed.buildings && BUILDINGS_VALUES.has(parsed.buildings)) params.buildings = parsed.buildings;
  else if (parsed.buildings != null) invalidKeys.push('buildings');

  if (parsed.local && LOCAL_VALUES.has(parsed.local)) params.local = parsed.local;
  else if (parsed.local != null) invalidKeys.push('local');

  const ev = normalizePercentLike(parsed.ev, 100);
  if (ev != null) params.ev = ev;
  else if (parsed.ev != null) invalidKeys.push('ev');

  const vmt = normalizePercentLike(parsed.vmt, 20);
  if (vmt != null) params.vmt = vmt;
  else if (parsed.vmt != null) invalidKeys.push('vmt');

  if (parsed.elecYear != null) params.elecYear = clamp(Math.round(parsed.elecYear), 2025, 2045);
  if (parsed.energyEfficiency != null) params.energyEfficiency = clamp(parsed.energyEfficiency, 0, 40);
  if (parsed.buildingsElectrification != null) params.buildingsElectrification = clamp(parsed.buildingsElectrification, 0, 100);
  if (parsed.organics != null) params.organics = clamp(parsed.organics, 50, 100);
  if (parsed.compactDevelopment != null) params.compactDevelopment = clamp(parsed.compactDevelopment, 0, 3);
  if (parsed.downtownHomes != null) params.downtownHomes = clamp(parsed.downtownHomes, 0, 100);
  if (parsed.activeTransportation != null) params.activeTransportation = clamp(parsed.activeTransportation, 0, 10);
  if (parsed.transitImprovements != null) params.transitImprovements = clamp(parsed.transitImprovements, 0, 50);
  if (parsed.parkingPricing != null) params.parkingPricing = clamp(parsed.parkingPricing, 0, 100);
  if (parsed.micromobility != null) params.micromobility = clamp(parsed.micromobility, 0, 200);
  if (parsed.weatherization != null) params.weatherization = clamp(parsed.weatherization, 0, 100);
  if (parsed.residentialReachCode != null) params.residentialReachCode = clamp(parsed.residentialReachCode, 0, 100);
  if (parsed.commercialReachCode != null) params.commercialReachCode = clamp(parsed.commercialReachCode, 0, 100);
  if (parsed.demandResponse != null) params.demandResponse = clamp(parsed.demandResponse, 0, 100);
  if (parsed.otherEfficiency != null) params.otherEfficiency = clamp(parsed.otherEfficiency, 0, 100);
  if (parsed.scenarioId) params.scenarioId = parsed.scenarioId;

  devLog('validated URL params', { params, invalidKeys });
  return { params, invalidKeys };
}

/**
 * Preset is applied first; explicit URL overrides win.
 */
export function applyScenarioPresetAndOverrides(
  validated: ScenarioUrlParams
): { preset?: LocalPresetParam; values: { ev?: number; vmt?: number; elecYear?: number } } {
  const preset = validated.local;
  const base = preset ? PRESET_DEFAULTS[preset] : undefined;
  const values = {
    ev: validated.ev ?? base?.ev,
    vmt: validated.vmt ?? base?.vmt,
    elecYear: validated.elecYear ?? base?.elecYear,
  };

  const hasOverride = validated.ev != null || validated.vmt != null || validated.elecYear != null;
  if (isDev()) {
    if (preset) devLog('preset loaded', { preset, presetValues: base });
    if (hasOverride) {
      devLog('overrides applied on top of preset', {
        ev: validated.ev,
        vmt: validated.vmt,
        elecYear: validated.elecYear,
      });
      if (preset) devLog('preset/override conflict resolved: explicit URL overrides win');
    }
  }

  return { preset, values };
}

export function serializeScenarioToUrlParams(state: ScenarioUrlParams): URLSearchParams {
  const params = new URLSearchParams();
  (Object.keys(state) as Array<keyof ScenarioUrlParams>).forEach((k) => {
    const value = state[k];
    if (value == null || value === '') return;
    params.set(k, String(value));
  });
  return params;
}

export function updateScenarioUrl(newState: ScenarioUrlParams): void {
  if (typeof window === 'undefined') return;
  const existing = parseScenarioFromUrl(window.location.search);
  const merged: ScenarioUrlParams = { ...existing, ...newState };
  (Object.keys(merged) as Array<keyof ScenarioUrlParams>).forEach((k) => {
    const value = merged[k];
    if (value == null || value === '') delete merged[k];
  });
  const query = serializeScenarioToUrlParams(merged).toString();
  const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
  window.history.replaceState({}, '', newUrl);
}

export function buildScenarioStateFromTool(input: ScenarioStateCore): ScenarioStateCore {
  return {
    location: input.location,
    emfacVersion: input.emfacVersion,
    buildingsBauScenario: input.buildingsBauScenario,
    localScenarioName: input.localScenarioName,
    interventionAreas: {
      vehicleElectrification: clamp(input.interventionAreas.vehicleElectrification, 0, 100),
      vmtReduction: clamp(input.interventionAreas.vmtReduction, 0, 20),
      energyEfficiency:
        input.interventionAreas.energyEfficiency != null
          ? clamp(input.interventionAreas.energyEfficiency, 0, 40)
          : undefined,
      buildingsElectrification:
        input.interventionAreas.buildingsElectrification != null
          ? clamp(input.interventionAreas.buildingsElectrification, 0, 100)
          : undefined,
      gridDecarbonization: clamp(Math.round(input.interventionAreas.gridDecarbonization), 2025, 2045),
      organicsDiversion:
        input.interventionAreas.organicsDiversion != null
          ? clamp(input.interventionAreas.organicsDiversion, 50, 100)
          : undefined,
    },
    subPolicyValues: input.subPolicyValues
      ? {
          compactDevelopment:
            input.subPolicyValues.compactDevelopment != null
              ? clamp(input.subPolicyValues.compactDevelopment, 0, 3)
              : undefined,
          downtownHomesPercent:
            input.subPolicyValues.downtownHomesPercent != null
              ? clamp(input.subPolicyValues.downtownHomesPercent, 0, 100)
              : undefined,
          activeTransportation:
            input.subPolicyValues.activeTransportation != null
              ? clamp(input.subPolicyValues.activeTransportation, 0, 10)
              : undefined,
          transitImprovements:
            input.subPolicyValues.transitImprovements != null
              ? clamp(input.subPolicyValues.transitImprovements, 0, 50)
              : undefined,
          parkingPricing:
            input.subPolicyValues.parkingPricing != null
              ? clamp(input.subPolicyValues.parkingPricing, 0, 100)
              : undefined,
          micromobility:
            input.subPolicyValues.micromobility != null
              ? clamp(input.subPolicyValues.micromobility, 0, 200)
              : undefined,
        }
      : undefined,
    energyEfficiencyComponents: input.energyEfficiencyComponents
      ? {
          weatherizationPercent:
            input.energyEfficiencyComponents.weatherizationPercent != null
              ? clamp(input.energyEfficiencyComponents.weatherizationPercent, 0, 100)
              : undefined,
          residentialReachCodePercent:
            input.energyEfficiencyComponents.residentialReachCodePercent != null
              ? clamp(input.energyEfficiencyComponents.residentialReachCodePercent, 0, 100)
              : undefined,
          commercialReachCodePercent:
            input.energyEfficiencyComponents.commercialReachCodePercent != null
              ? clamp(input.energyEfficiencyComponents.commercialReachCodePercent, 0, 100)
              : undefined,
          demandResponsePercent:
            input.energyEfficiencyComponents.demandResponsePercent != null
              ? clamp(input.energyEfficiencyComponents.demandResponsePercent, 0, 100)
              : undefined,
          otherEfficiencyPercent:
            input.energyEfficiencyComponents.otherEfficiencyPercent != null
              ? clamp(input.energyEfficiencyComponents.otherEfficiencyPercent, 0, 100)
              : undefined,
        }
      : undefined,
  };
}

export function serializeScenarioState(state: ScenarioStateCore): Record<string, unknown> {
  return {
    location: state.location,
    transportScenario: state.emfacVersion === '2025' ? 'emfac2025' : 'emfac2021',
    buildingsScenario: state.buildingsBauScenario === 'current_policy' ? 'bau' : state.buildingsBauScenario,
    localPreset: state.localScenarioName,
    interventionAreas: { ...state.interventionAreas },
    subPolicyValues: state.subPolicyValues ? { ...state.subPolicyValues } : undefined,
    energyEfficiencyComponents: state.energyEfficiencyComponents ? { ...state.energyEfficiencyComponents } : undefined,
  };
}

export function hydrateToolFromScenarioState(raw: Record<string, unknown>): Partial<ScenarioStateCore> {
  const interventionRaw = (raw.interventionAreas ?? {}) as Record<string, unknown>;
  const subPolicyRaw = (raw.subPolicyValues ?? {}) as Record<string, unknown>;
  const efficiencyRaw = (raw.energyEfficiencyComponents ?? {}) as Record<string, unknown>;
  const out: Partial<ScenarioStateCore> = {};

  if (typeof raw.location === 'string') out.location = raw.location;
  if (raw.transportScenario === 'emfac2021' || raw.transportScenario === 'emfac2025') {
    out.emfacVersion = raw.transportScenario === 'emfac2025' ? '2025' : '2021';
  }
  if (
    raw.buildingsScenario === 'bau' ||
    raw.buildingsScenario === 'current_policy' ||
    raw.buildingsScenario === 'conservative' ||
    raw.buildingsScenario === 'accelerated'
  ) {
    out.buildingsBauScenario = raw.buildingsScenario === 'bau' ? 'current_policy' : raw.buildingsScenario;
  }
  if (
    raw.localPreset === 'baseline' ||
    raw.localPreset === 'moderate' ||
    raw.localPreset === 'aggressive' ||
    raw.localPreset === 'fullImplementation'
  ) {
    out.localScenarioName = raw.localPreset;
  }

  out.interventionAreas = {
    vehicleElectrification: clamp(Number(interventionRaw.vehicleElectrification) || 0, 0, 100),
    vmtReduction: clamp(Number(interventionRaw.vmtReduction) || 0, 0, 20),
    energyEfficiency:
      interventionRaw.energyEfficiency != null
        ? clamp(Number(interventionRaw.energyEfficiency) || 0, 0, 40)
        : undefined,
    buildingsElectrification:
      interventionRaw.buildingsElectrification != null
        ? clamp(Number(interventionRaw.buildingsElectrification) || 0, 0, 100)
        : undefined,
    gridDecarbonization: clamp(Math.round(Number(interventionRaw.gridDecarbonization) || 2045), 2025, 2045),
    organicsDiversion:
      interventionRaw.organicsDiversion != null
        ? clamp(Number(interventionRaw.organicsDiversion) || 50, 50, 100)
        : undefined,
  };
  out.subPolicyValues = {
    compactDevelopment:
      subPolicyRaw.compactDevelopment != null ? clamp(Number(subPolicyRaw.compactDevelopment) || 0, 0, 3) : undefined,
    downtownHomesPercent:
      subPolicyRaw.downtownHomesPercent != null ? clamp(Number(subPolicyRaw.downtownHomesPercent) || 70, 0, 100) : undefined,
    activeTransportation:
      subPolicyRaw.activeTransportation != null ? clamp(Number(subPolicyRaw.activeTransportation) || 0, 0, 10) : undefined,
    transitImprovements:
      subPolicyRaw.transitImprovements != null ? clamp(Number(subPolicyRaw.transitImprovements) || 0, 0, 50) : undefined,
    parkingPricing:
      subPolicyRaw.parkingPricing != null ? clamp(Number(subPolicyRaw.parkingPricing) || 0, 0, 100) : undefined,
    micromobility:
      subPolicyRaw.micromobility != null ? clamp(Number(subPolicyRaw.micromobility) || 0, 0, 200) : undefined,
  };
  out.energyEfficiencyComponents = {
    weatherizationPercent:
      efficiencyRaw.weatherizationPercent != null ? clamp(Number(efficiencyRaw.weatherizationPercent) || 0, 0, 100) : undefined,
    residentialReachCodePercent:
      efficiencyRaw.residentialReachCodePercent != null
        ? clamp(Number(efficiencyRaw.residentialReachCodePercent) || 0, 0, 100)
        : undefined,
    commercialReachCodePercent:
      efficiencyRaw.commercialReachCodePercent != null
        ? clamp(Number(efficiencyRaw.commercialReachCodePercent) || 0, 0, 100)
        : undefined,
    demandResponsePercent:
      efficiencyRaw.demandResponsePercent != null ? clamp(Number(efficiencyRaw.demandResponsePercent) || 0, 0, 100) : undefined,
    otherEfficiencyPercent:
      efficiencyRaw.otherEfficiencyPercent != null ? clamp(Number(efficiencyRaw.otherEfficiencyPercent) || 0, 0, 100) : undefined,
  };
  return out;
}
